import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import {
  Button,
  NewBadgeSetting,
  SettingsActions,
  SettingsEmpty,
  SettingsInput,
  SettingsMessage,
  SettingsSave,
  SettingsSection,
  SettingsTable,
  ViewDefaultSetting,
  useDeclarationsPort,
  useSettingsDoor,
} from '@taki/ui';
import type { SettingsDoor } from '@taki/ui';
import { cleDeSequence, formaterNumero } from '@taki/data-model';
import type { AppField, EtatDeSequence, SequenceDeNumero } from '@taki/data-model';
import type { TakiSdk } from '@taki/sdk';
import type { AppDeclaree } from './app-declaree.js';
import { assemblerEcran } from './assemblage.js';
import type { EcranAssemble } from './assemblage.js';
import { ReglageDemo } from './ReglageDemo.js';
import { vuesDeLApp } from './vues-de-fiche.js';
import { jeuxDesInstances, resoudreDeclaration } from './resolution.js';
import { useAppRuntime } from './runtime.js';

/**
 * L'ETAT sous « Ouvrir les fiches sur » quand un clic de la porte entre dans
 * une porte liee (decision D2) : ce que fait le clic, en un etat et non en
 * phrase (regle 1 des Parametres sobres, `core/parametres-sobres`).
 */
export function aideDuClicLie(porteLiee: string): string {
  return `Ouvre : ${porteLiee}`;
}

/**
 * LES REGLAGES D'UNE APP DECLAREE, montes par le hub Parametres. Chaque reglage
 * vit la ou il s'applique (G11, `docs/GABARIT-ECRAN.md`). L'app (porte nulle) :
 * « Mon affichage » avec les nouveautes, qui valent pour toute l'app, puis son
 * jeu de demonstration. Une porte : « Mon affichage » avec sa vue d'arrivee et
 * la vue ou s'ouvrent ses fiches, puis « Séquences » quand son jeu porte un
 * numero automatique. Les Étiquettes et listes et le Studio sont poses par le
 * hub.
 */
export function ReglagesDeclares({ app }: { app: AppDeclaree }): JSX.Element | null {
  const door = useSettingsDoor();
  return door === null ? <ReglagesGeneraux app={app} /> : <MonAffichage app={app} door={door} />;
}

/** La declaration que l'app monte vraiment (la sienne, ou celle de l'org). */
function useResolution(app: AppDeclaree): ReturnType<typeof resoudreDeclaration> {
  const { instances } = useAppRuntime();
  return useMemo(() => resoudreDeclaration(app, instances?.[app.id], jeuxDesInstances(instances)), [app, instances]);
}

function ReglagesGeneraux({ app }: { app: AppDeclaree }): JSX.Element {
  const { declaration } = useResolution(app);
  return (
    <>
      <SettingsSection title="Mon affichage">
        <NewBadgeSetting
          appId={app.id}
          screenId={declaration.ecran.id}
          screenIds={declaration.ecrans.ecrans.map((ecran) => ecran.id)}
          label="Nouveautés de l’app"
        />
      </SettingsSection>
      <ReglageDemo app={app} />
    </>
  );
}

function MonAffichage({ app, door }: { app: AppDeclaree; door: SettingsDoor }): JSX.Element {
  const { sdk, orgId = '' } = useAppRuntime();
  const resolution = useResolution(app);

  const declaration = useMemo(() => {
    const courante = resolution.declaration;
    const ecran = courante.ecrans.ecrans.find((candidate) => candidate.id === door.screenId);
    return ecran === undefined || ecran === courante.ecran ? courante : { ...courante, ecran };
  }, [resolution.declaration, door.screenId]);

  const vuesEnPlus = useMemo(() => vuesDeLApp(declaration.ecran, app.canvas), [declaration.ecran, app.canvas]);
  /* Meme assemblage que l'ecran : les vues indisponibles gardent leur raison. */
  const assemble: EcranAssemble = useMemo(
    () => assemblerEcran(declaration, sdk, app.fonctions, app.effets, undefined, vuesEnPlus),
    [declaration, sdk, app.fonctions, app.effets, vuesEnPlus],
  );

  const porte = assemble.portes.find((candidate) => candidate.id === door.doorId);
  const bindings = useMemo(() => assemble.bindings(door.doorId), [assemble, door.doorId]);
  if (door.screenId !== assemble.ecran.id || porte === undefined) {
    return <SettingsEmpty>Cette porte n’est plus disponible.</SettingsEmpty>;
  }
  const types = porte.viewIds === undefined ? assemble.types : assemble.types.filter((type) => porte.viewIds!.includes(type.id));
  /* UN CLIC QUI ENTRE DANS UNE PORTE LIEE (`auClic: { lien }`) n'ouvre pas la
     fiche (decision du 27/10, D2) : le choix vaut alors pour les fiches
     ouvertes autrement (adresse, recherche, lien), et la ligne dit OU entre
     le clic, en un etat (« Ouvre : Tâches »). Sans clic entrant, aucune aide :
     la page est celle de la porte, la portee va de soi (regle 4). */
  const porteLiee = porte.auClic === undefined
    ? undefined
    : assemble.portes.find((candidate) => candidate.id === porte.auClic!.link.door)?.label ?? porte.auClic.link.door;

  const jeuDeLaPorte = declaration.ecran.portes.find((candidate) => candidate.id === door.doorId)?.jeu;
  return (
    <>
    <SettingsSection title="Mon affichage">
      <ViewDefaultSetting
        key={`${assemble.ecran.id}:${door.doorId}`}
        appId={app.id}
        screenId={assemble.ecran.id}
        doorId={door.doorId}
        types={types}
        bindings={bindings}
        label="Ouvrir sur"
      />
      {/* LA VUE OU S'OUVRENT SES FICHES (chantier `fiche-suivi`) : le choix de
          chacun, qui l'emporte sur le defaut de la porte (`auClic`). Une porte
          sans autre vue de fiche que « Fiche » le dit en lecture. */}
      <ViewDefaultSetting
        key={`${assemble.ecran.id}:${door.doorId}:fiche`}
        appId={app.id}
        screenId={assemble.ecran.id}
        doorId={door.doorId}
        types={types}
        bindings={bindings}
        level="record"
        label="Ouvrir les fiches sur"
        {...(porteLiee === undefined ? {} : { hint: aideDuClicLie(porteLiee) })}
      />
    </SettingsSection>
    {jeuDeLaPorte === undefined ? null : <NumerosDuJeu sdk={sdk} orgId={orgId} champs={declaration.champs} jeu={jeuDeLaPorte} />}
    </>
  );
}

/**
 * « SÉQUENCES » : le prochain numero de chaque champ `numero` du jeu de la
 * porte (`docs/socle/champs-types.md`, section 4), a cote d'Étiquettes et
 * listes. UN tableau du kit (`SettingsTable`), une ligne par numero
 * automatique : Champ, Dernier attribué, Prochain (la case), Aperçu. UN SEUL
 * Annuler / Enregistrer sous le tableau, qui n'ecrit que les sequences
 * changees (retouche de la recette du 27/10, validee a l'ecran).
 *
 * C'EST UN ETAT, PAS UNE DECLARATION : le prochain numero vit dans
 * `orgs/{orgId}/sequences/{cle}`, il se lit et s'ecrit par `sdk.sequences`,
 * tout de suite, sans passer par le Studio ni par une publication. Le modele
 * du numero, lui, reste au Studio (section du champ).
 *
 * RESERVE AUX ADMINISTRATEURS, comme le reste de ces reglages : le droit est
 * celui du port des declarations (`peutModifier`, owner ou admin actif), et sa
 * raison se dit UNE fois, au survol du cadenas de la section. Les rules
 * refusent de toute facon un prochain numero inferieur ou egal au dernier
 * attribue, et le SDK le dit avant (`reglerProchain`) : la phrase de refus est
 * la sienne, affichee telle quelle, sous le titre.
 *
 * UN CHAMP MASQUE (`jeux[].masques`) n'a pas de ligne : il ne se montre sur
 * aucune fiche, son numero ne se regle pas ici non plus.
 */
function NumerosDuJeu({ sdk, orgId, champs, jeu }: {
  sdk: TakiSdk;
  orgId: string;
  champs: { jeux: readonly { family: string; type?: string; champs: readonly AppField[]; masques?: readonly string[] }[] };
  jeu: { family: string; type?: string };
}): JSX.Element | null {
  const port = useDeclarationsPort();
  const declare = champs.jeux.find((candidat) => candidat.family === jeu.family && (candidat.type ?? '') === (jeu.type ?? ''));
  const masques = new Set(declare?.masques ?? []);
  const sequences: LigneDeSequence[] = (declare?.champs ?? [])
    .filter((champ) => champ.type === 'numero' && !masques.has(champ.key))
    .map((champ) => ({
      cle: cleDeSequence({ family: jeu.family, ...(jeu.type === undefined ? {} : { type: jeu.type }), key: champ.key }),
      label: champ.label,
      sequence: champ.sequence,
    }));
  if (sequences.length === 0) return null;
  const peut = port?.peutModifier === true;
  /* LA SIGNATURE DU JEU DE SEQUENCES : une chaine, donc stable d'un rendu a
     l'autre. Elle remonte le tableau quand les numeros changent (etat neuf) et
     c'est d'elle que l'effet tire les clefs a lire. */
  const signature = JSON.stringify(sequences.map((ligne) => ligne.cle));
  return (
    <SettingsSection title="Séquences" contextLabel={porteeDesReglages(orgId)} {...(peut ? {} : { lock: 'Réservé aux administrateurs.' })}>
      <TableDesSequences key={signature} signature={signature} sdk={sdk} peut={peut} sequences={sequences} />
    </SettingsSection>
  );
}

/**
 * LA PORTEE D'UN REGLAGE DE PORTE, a droite du titre : le MEME mot que la
 * section Étiquettes et listes posee juste dessous par le hub
 * (`packages/shell/src/screens/SettingsHubScreen.tsx`, `ReglagesApp`). Sur
 * `master`, l'org de reference, les Parametres d'une app sont ceux de son
 * modele ; ailleurs, ceux de l'organisation (son instance). LA PORTEE SEULE :
 * qui peut regler (les administrateurs) est la raison du verrou, dite une
 * fois, au survol du cadenas (`lock`, regle R2 de `core/parametres-sobres`).
 */
function porteeDesReglages(orgId: string): string {
  return orgId === 'master' ? 'Modèle' : 'Organisation';
}

interface LigneDeSequence { cle: string; label: string; sequence: SequenceDeNumero | undefined }

/** Les sequences lues : `null` pendant la lecture, `'echec'` si elle a echoue. */
type Lues = Readonly<Record<string, EtatDeSequence | null>> | null | 'echec';

const ENTIER = /^\d+$/;

function TableDesSequences({ signature, sdk, peut, sequences }: {
  signature: string;
  sdk: TakiSdk;
  peut: boolean;
  sequences: readonly LigneDeSequence[];
}): JSX.Element {
  const [lues, setLues] = useState<Lues>(null);
  const [saisies, setSaisies] = useState<Readonly<Record<string, string>>>({});
  const [occupe, setOccupe] = useState(false);
  const [fait, setFait] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  useEffect(() => {
    let vivant = true;
    const cles = JSON.parse(signature) as string[];
    Promise.all(cles.map(async (cle) => [cle, await sdk.sequences.lire(cle)] as const))
      .then((lus) => {
        if (!vivant) return;
        setLues(Object.fromEntries(lus));
        setSaisies(Object.fromEntries(lus.map(([cle, lu]) => [cle, String(lu?.prochain ?? 1)])));
      })
      .catch((cause: unknown) => {
        if (vivant) { setLues('echec'); setErreur(cause instanceof Error ? cause.message : String(cause)); }
      });
    return () => { vivant = false; };
  }, [sdk, signature]);

  const table = lues === null || lues === 'echec' ? null : lues;
  const pret = table !== null;
  const lu = (cle: string): EtatDeSequence | null => table?.[cle] ?? null;
  const courant = (cle: string): string => String(lu(cle)?.prochain ?? 1);
  const saisie = (cle: string): string => (saisies[cle] ?? '').trim();
  const entier = (cle: string): boolean => ENTIER.test(saisie(cle));
  const changees = pret ? sequences.filter((ligne) => saisie(ligne.cle) !== courant(ligne.cle)) : [];
  const invalide = changees.some((ligne) => !entier(ligne.cle));
  const fige = !peut || occupe || !pret;

  const annuler = (): void => {
    setSaisies(Object.fromEntries(sequences.map((ligne) => [ligne.cle, courant(ligne.cle)])));
    setErreur(null);
  };
  const enregistrer = async (): Promise<void> => {
    if (fige || changees.length === 0 || invalide) return;
    setOccupe(true); setErreur(null); setFait(false);
    try {
      /* L'UNE APRES L'AUTRE : une sequence refusee arrete la suite, et celles
         deja ecrites ne comptent plus comme changees. */
      for (const ligne of changees) {
        const prochain = Number(saisie(ligne.cle));
        await sdk.sequences.reglerProchain(ligne.cle, prochain);
        setLues((avant) => {
          const base = avant === null || avant === 'echec' ? {} : avant;
          return { ...base, [ligne.cle]: { prochain, dernier: base[ligne.cle]?.dernier ?? null } };
        });
      }
      setFait(true);
    } catch (cause) {
      setErreur(cause instanceof Error ? cause.message : String(cause));
    } finally { setOccupe(false); }
  };

  /* CE QUE LE PROCHAIN NUMERO DONNERA, avec le modele du champ : « FAC-2026-0043 »
     se lit mieux que « 43 ». L'annee est celle du jour, comme a l'attribution.
     Une saisie qui n'est pas un entier : la case le dit (`aria-invalid`), et
     l'Aperçu n'a rien a montrer. */
  const maintenant = new Date();
  const rows = sequences.map((ligne) => {
    const dernier = lu(ligne.cle)?.dernier;
    const valide = entier(ligne.cle);
    return {
      id: ligne.cle,
      cells: [
        <span key="champ" className="font-medium text-ink-900">{ligne.label}</span>,
        <span key="dernier" className="text-ink-500">{lues === null ? 'Lecture…' : dernier == null ? 'Aucun' : String(dernier)}</span>,
        <SettingsInput key="prochain" className="w-24" inputMode="numeric" aria-label={`Prochain numéro de ${ligne.label}`}
          value={saisies[ligne.cle] ?? ''} disabled={fige} aria-invalid={pret && !valide}
          onChange={(event) => {
            const valeur = event.target.value;
            setSaisies((avant) => ({ ...avant, [ligne.cle]: valeur }));
            setFait(false); setErreur(null);
          }} />,
        <span key="apercu" className={valide ? 'font-mono text-ink-700' : 'text-danger'}>
          {!pret ? '' : valide ? formaterNumero(ligne.sequence, Number(saisie(ligne.cle)), maintenant) : 'Invalide'}
        </span>,
      ],
    };
  });
  return (
    <>
      {erreur ? <SettingsMessage tone="error">{erreur}</SettingsMessage> : null}
      <SettingsTable label="Séquences" disabled={!peut} rows={rows}
        columns={[{ label: 'Champ' }, { label: 'Dernier attribué' }, { label: 'Prochain', largeur: 'w-32' }, { label: 'Aperçu' }]} />
      <SettingsActions>
        <Button variant="ghost" size="sm" disabled={occupe || changees.length === 0} onClick={annuler}>Annuler</Button>
        <SettingsSave busy={occupe} done={fait && changees.length === 0} disabled={fige || changees.length === 0 || invalide}
          onSave={() => void enregistrer()} />
      </SettingsActions>
    </>
  );
}

export default ReglagesDeclares;
